import { Draggable } from "@hello-pangea/dnd";
import { AlertTriangle, ListTodo } from "lucide-react";
import { useTranslate } from "ra-core";
import type { CSSProperties } from "react";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import type { PipelineCardSummary, Sale, Tag } from "../../types";
import { getSaleName } from "../useActiveSales";
import { getFullName } from "../cardModel";
import { PriorityBadge } from "../PriorityBadge";
import { useFormatAmount } from "../useFormatAmount";

const MAX_TAGS = 3;

export interface PipelineCardItemProps {
  card: PipelineCardSummary;
  index: number;
  tagsById: Map<number, Tag>;
  salesById: Map<Sale["id"], Sale>;
  onOpen: (card: PipelineCardSummary) => void;
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

export const PipelineCardItem = ({
  card,
  index,
  tagsById,
  salesById,
  onOpen,
}: PipelineCardItemProps) => {
  const translate = useTranslate();
  const formatAmount = useFormatAmount();
  const name = getFullName(card) || translate("crm.operations.cards.unnamed");
  const tags = (card.tags ?? []).flatMap((tagId) => {
    const tag = tagsById.get(tagId);
    return tag ? [tag] : [];
  });
  const assignee = getSaleName(salesById.get(card.sales_id as Sale["id"]));

  return (
    <Draggable draggableId={String(card.id)} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={provided.draggableProps.style as CSSProperties}
          onClick={() => onOpen(card)}
          aria-label={name}
          data-testid="pipeline-card"
        >
          <Card
            className={cn(
              "gap-2 px-3 py-2.5 transition-shadow",
              snapshot.isDragging
                ? "rotate-1 shadow-lg"
                : "shadow-xs hover:shadow-md",
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <span className="text-sm font-medium leading-tight">{name}</span>
              {card.priority !== "low" && (
                <PriorityBadge priority={card.priority} />
              )}
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {tags.slice(0, MAX_TAGS).map((tag) => (
                  <span
                    key={tag.id}
                    className="rounded px-1.5 py-0.5 text-[11px] text-black"
                    style={{ backgroundColor: tag.color }}
                  >
                    {tag.name}
                  </span>
                ))}
                {tags.length > MAX_TAGS && (
                  <span className="text-[11px] text-muted-foreground">
                    +{tags.length - MAX_TAGS}
                  </span>
                )}
              </div>
            )}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {assignee && (
                <span
                  className="flex size-6 items-center justify-center rounded-full bg-muted text-[10px] font-semibold"
                  title={assignee}
                >
                  {initials(assignee)}
                </span>
              )}
              {card.deals_amount > 0 && (
                <span className="font-medium text-foreground">
                  {formatAmount(card.deals_amount)}
                </span>
              )}
              <span className="ml-auto flex items-center gap-2">
                {card.nb_overdue_tasks > 0 && (
                  <AlertTriangle
                    className="size-3.5 text-destructive"
                    aria-label={translate("crm.operations.cards.overdue_tasks")}
                  />
                )}
                {card.nb_open_tasks > 0 && (
                  <span
                    className="flex items-center gap-0.5"
                    title={translate("crm.operations.cards.open_tasks", {
                      smart_count: card.nb_open_tasks,
                    })}
                  >
                    <ListTodo className="size-3.5" />
                    {card.nb_open_tasks}
                  </span>
                )}
              </span>
            </div>
          </Card>
        </div>
      )}
    </Draggable>
  );
};
